/**
 * Centralized Logging Utility for PolarRide Frontend
 *
 * USAGE FOR CLAUDE AGENTS:
 *
 * 1. Import the logger:
 *    import { logger } from '@/utils/logger';
 *
 * 2. Create a module-specific logger:
 *    const log = logger.module('BookingService');
 *
 * 3. Use appropriate log levels:
 *    log.debug('Detailed debug info', { data });  // Only in dev mode
 *    log.info('Important events', { user });      // In dev mode
 *    log.warn('Warning messages', { issue });     // In dev and prod
 *    log.error('Error occurred', error);          // Always logged
 *
 * 4. Toggle module logging in browser console:
 *    window.setLogLevel('BookingService', 'debug');
 *    window.setLogLevel('BookingService', 'off');
 *
 * ENVIRONMENT BEHAVIOR:
 * - Development: debug, info, warn, error all work
 * - Production: only warn and error work (info/debug are silent)
 * - Test: all logs are silent unless explicitly enabled
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'off';

interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
  modules: Record<string, LogLevel>;
}

class Logger {
  private config: LoggerConfig = {
    enabled: true,
    level: this.getDefaultLogLevel(),
    modules: {}
  };

  private levelPriority: Record<LogLevel, number> = {
    debug: 0,
    info: 1,
    warn: 2,
    error: 3,
    off: 4
  };

  constructor() {
    // Expose logger controls to window for runtime debugging
    if (typeof window !== 'undefined') {
      (window as any).setLogLevel = this.setModuleLevel.bind(this);
      (window as any).getLogConfig = () => this.config;
    }
  }

  private getDefaultLogLevel(): LogLevel {
    const env = import.meta.env.MODE;

    if (env === 'production') {
      return 'warn'; // Production: only warn and error
    } else if (env === 'test') {
      return 'off'; // Tests: silent by default
    } else {
      return 'debug'; // Development: all logs
    }
  }

  private shouldLog(moduleLevel: LogLevel, messageLevel: LogLevel): boolean {
    if (!this.config.enabled) return false;

    const modulePriority = this.levelPriority[moduleLevel];
    const messagePriority = this.levelPriority[messageLevel];

    return messagePriority >= modulePriority;
  }

  /**
   * Set log level for a specific module at runtime
   * Usage in browser console: window.setLogLevel('BookingService', 'debug')
   */
  setModuleLevel(moduleName: string, level: LogLevel): void {
    this.config.modules[moduleName] = level;
    console.info(`📝 Logger: Set ${moduleName} to level "${level}"`);
  }

  /**
   * Create a module-specific logger
   *
   * @example
   * const log = logger.module('UserService');
   * log.info('User logged in', { userId: '123' });
   */
  module(name: string) {
    const moduleLevel = this.config.modules[name] || this.config.level;

    return {
      /**
       * Debug-level logging - detailed diagnostic information
       * Only appears in development mode
       */
      debug: (message: string, ...args: any[]) => {
        if (this.shouldLog(moduleLevel, 'debug')) {
          console.debug(`🔍 [${name}] ${message}`, ...args);
        }
      },

      /**
       * Info-level logging - general informational messages
       * Appears in development mode
       */
      info: (message: string, ...args: any[]) => {
        if (this.shouldLog(moduleLevel, 'info')) {
          console.info(`ℹ️ [${name}] ${message}`, ...args);
        }
      },

      /**
       * Warning-level logging - potentially harmful situations
       * Appears in development AND production
       */
      warn: (message: string, ...args: any[]) => {
        if (this.shouldLog(moduleLevel, 'warn')) {
          console.warn(`⚠️ [${name}] ${message}`, ...args);
        }
      },

      /**
       * Error-level logging - error events
       * Always appears (dev and production)
       */
      error: (message: string, ...args: any[]) => {
        if (this.shouldLog(moduleLevel, 'error')) {
          console.error(`❌ [${name}] ${message}`, ...args);
        }
      },

      /**
       * Trace performance timing
       *
       * @example
       * const end = log.time('API Call');
       * await fetchData();
       * end();
       */
      time: (label: string) => {
        if (this.shouldLog(moduleLevel, 'debug')) {
          const startTime = performance.now();
          return () => {
            const duration = performance.now() - startTime;
            console.debug(`⏱️ [${name}] ${label}: ${duration.toFixed(2)}ms`);
          };
        }
        return () => {}; // No-op if logging disabled
      },

      /**
       * Group related logs together
       *
       * @example
       * log.group('Processing batch', () => {
       *   log.info('Item 1');
       *   log.info('Item 2');
       * });
       */
      group: (label: string, fn: () => void) => {
        if (this.shouldLog(moduleLevel, 'debug')) {
          console.group(`📦 [${name}] ${label}`);
          try {
            fn();
          } finally {
            console.groupEnd();
          }
        } else {
          fn(); // Execute without grouping if logging disabled
        }
      }
    };
  }
}

// Export singleton instance
export const logger = new Logger();

/**
 * Helper function to create a logger from file path
 * Automatically extracts module name from import.meta.url
 *
 * @example
 * // In BookingService.ts
 * const log = createLogger(import.meta.url);
 * // Automatically becomes: logger.module('BookingService')
 */
export function createLogger(fileUrl: string) {
  // Extract filename from path: /path/to/BookingService.ts -> BookingService
  const fileName = fileUrl.split('/').pop()?.replace(/\.(ts|tsx|js|jsx)$/, '') || 'Unknown';
  return logger.module(fileName);
}

/**
 * QUICK EXAMPLES FOR CLAUDE AGENTS:
 *
 * // In a service file:
 * import { logger } from '@/utils/logger';
 * const log = logger.module('BookingService');
 *
 * export const fetchBookings = async () => {
 *   log.info('Fetching bookings');
 *
 *   try {
 *     const response = await api.get('/bookings');
 *     log.debug('Bookings fetched', { count: response.data.length });
 *     return response.data;
 *   } catch (error) {
 *     log.error('Failed to fetch bookings', error);
 *     throw error;
 *   }
 * };
 *
 * // Enable debug logs for specific module at runtime (in browser console):
 * window.setLogLevel('BookingService', 'debug');
 *
 * // Disable all logs for a module:
 * window.setLogLevel('BookingService', 'off');
 *
 * // See current log configuration:
 * window.getLogConfig();
 */
